import React from "react";
import { Plus, Clock } from "lucide-react";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function addDays(d, n) {
    const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt;
}
function fmtISO(d) {
    return new Date(d).toISOString().slice(0, 10);
}
function isSameISO(a, b) { return a === b; }

function minutesToHours(m) {
    if (!m) return "0h";
    const h = Math.floor(m / 60), mm = m % 60;
    return mm ? `${h}h ${mm}m` : `${h}h`;
}

export default function ShiftWeekGrid({ weekStart, shifts, users, isAdmin, onCreate, onEdit }) {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const dayISO = days.map(fmtISO);
    const today = fmtISO(new Date());

    // Index shifts by user_id + date for O(1) cell lookup
    const cell = {};
    for (const s of shifts) {
        cell[`${s.user_id}|${s.date}`] = cell[`${s.user_id}|${s.date}`] || [];
        cell[`${s.user_id}|${s.date}`].push(s);
    }

    if (!users || users.length === 0) {
        return (
            <div className="p-8 text-center text-sm text-neutral-500">
                Keine Mitarbeiter gefunden.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto" data-testid="shift-week-grid">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="text-left px-3 py-2.5 font-semibold w-44 sticky left-0 bg-neutral-50 z-10">Mitarbeiter</th>
                        {days.map((d, i) => (
                            <th
                                key={i}
                                className={`text-left px-3 py-2.5 font-semibold ${dayISO[i] === today ? "bg-neutral-900 text-white" : ""}`}
                            >
                                <div>{DAYS[i]}</div>
                                <div className="text-xs font-normal opacity-80">
                                    {d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {users.map((u) => {
                        let weekMinutes = 0;
                        return (
                            <tr key={u.id} className="border-b border-neutral-100 hover:bg-neutral-50/50">
                                <td className="px-3 py-2.5 font-medium sticky left-0 bg-white z-10 border-r border-neutral-100">
                                    <div className="truncate" title={u.email}>{u.name || u.email}</div>
                                    <div className="text-xs text-neutral-500 mt-0.5">{u.role}</div>
                                </td>
                                {dayISO.map((iso, i) => {
                                    const cellShifts = cell[`${u.id}|${iso}`] || [];
                                    cellShifts.forEach((s) => { weekMinutes += s.worked_minutes || 0; });
                                    return (
                                        <td
                                            key={i}
                                            className={`px-1 py-1.5 align-top relative ${iso === today ? "bg-neutral-50" : ""}`}
                                            style={{ minWidth: 100 }}
                                            data-testid={`shift-cell-${u.id}-${iso}`}
                                        >
                                            {cellShifts.map((s) => (
                                                <button
                                                    key={s.id}
                                                    onClick={() => isAdmin && onEdit(s)}
                                                    disabled={!isAdmin}
                                                    className="w-full text-left rounded-md bg-neutral-900 text-white px-2 py-1 mb-1 hover:bg-neutral-700 transition-colors group"
                                                    data-testid={`shift-block-${s.id}`}
                                                >
                                                    <div className="text-xs font-semibold tabular-nums flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {s.start_time}–{s.end_time}
                                                    </div>
                                                    <div className="text-[10px] opacity-70 mt-0.5">
                                                        {minutesToHours(s.worked_minutes)}
                                                        {s.breaks && s.breaks.length > 0 && ` · ${s.breaks.length} Pause`}
                                                    </div>
                                                </button>
                                            ))}
                                            {isAdmin && (
                                                <button
                                                    onClick={() => onCreate({ user_id: u.id, date: iso })}
                                                    className="w-full opacity-0 group-hover:opacity-100 hover:opacity-100 text-xs text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-md py-1 flex items-center justify-center transition-opacity"
                                                    data-testid={`shift-add-${u.id}-${iso}`}
                                                    aria-label="Schicht hinzufügen"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
